// types/intentionPeriods.ts - Types for teaching intention submission periods

export type IntentionPeriodStatus = 'draft' | 'open' | 'closed' | 'archived';

export interface IntentionPeriod {
  id: string;
  name: string;                    // e.g., "2027 Teaching Intentions"
  academicYear: number;            // e.g., 2027
  opensAt: Date;
  dueDate?: Date;                  // Public deadline — submissions after this are marked "late"
  closesAt: Date;                  // Hard close — form becomes read-only after this
  status: IntentionPeriodStatus;
  description?: string;            // Instructions for staff
  createdBy?: string;              // Staff UUID who created the period
  createdAt: Date;
  updatedAt: Date;
}

export interface IntentionPeriodFormData {
  name: string;
  academicYear: number;
  opensAt: string;                 // ISO date string for form input
  dueDate?: string;                // ISO date string — public deadline
  closesAt: string;                // ISO date string — hard close
  status: IntentionPeriodStatus;
  description?: string;
}

// HR Review types
export type HRReviewStatus = 'draft' | 'submitted' | 'hr_approved' | 'hr_flagged';

export interface HRReviewData {
  hrReviewedBy?: string;           // Staff UUID who reviewed
  hrReviewedAt?: Date;
  hrNotes?: string;
}

// Submission statistics for a period
export interface IntentionPeriodStats {
  periodId: string;
  totalStaff: number;
  totalSubmissions: number;
  draftCount: number;
  submittedCount: number;
  hrApprovedCount: number;
  hrFlaggedCount: number;
  pendingReviewCount: number;
  submissionRate: number;          // Percentage of staff who submitted
}

// Employment type options
export type EmploymentType = 'ongoing' | 'fixed-term';

// Return/consideration intention options
export type IntentionResponse = 'yes' | 'no' | 'uncertain';

// Leave type options
export type LeaveType = 'long-service' | 'leave-without-pay' | 'other' | null;

// TIL Activity options - based on CEMEA (2022) requirements
export const TIL_ACTIVITIES = [
  'Year 7 camp',
  'Thursday academic/behaviour detentions',
  'Thursday afternoon Community Activities',
  'Rupo Harvest',
  'Presentation Ball',
  'Parent information evenings',
  'Catholic Identity initiatives',
  'After school homework help',
  'School production',
  'Grade 6 testing',
  'Year 7 interviews',
  'Year 9 stretch camp',
  'Twilight school PM activities',
  'Outdoor ed/Duke of Ed camps'
] as const;

export type TILActivity = typeof TIL_ACTIVITIES[number];

// Employment details data structure for JSONB
export interface EmploymentDetailsData {
  // Employment type (drives conditional logic)
  employmentType: EmploymentType;

  // For Ongoing staff: "Do you intend to return next year?"
  intentionToReturn: IntentionResponse | null;

  // For Fixed-Term staff: "Do you wish to be considered for employment?"
  wishToBeConsidered: IntentionResponse | null;

  // If No or Uncertain, reason text
  notReturningReason?: string;

  // Structured leave information
  hasLeavePlans: boolean;
  leaveType?: LeaveType;
  leaveStartDate?: string;  // ISO date string
  leaveEndDate?: string;    // ISO date string

  // Special role interest — Year 9 Stretch (formerly "Retreat")
  year9StretchInterest: boolean | null;

  // TIL activities (minimum 5 required if returning/being considered)
  tilActivities: string[];
}

// FTE preference — locked to these options only
export type FtePreference = '1' | '0.9' | '0.8' | '0.75' | '0.6' | '0.5' | '0.4' | 'discuss';

// Day-off preference — which options depend on FTE:
// - fte '0.5' / '0.6' (two days off): 'wed-thu' | 'mon-fri' | 'none' | 'discuss'
// - fte '0.75' / '0.8' (one day off): 'wed-only' | 'fri-only' | 'none' | 'discuss'
// - fte '1' (no days off):        n/a (field should be undefined)
// - fte '0.4' (no day choice):    n/a (field should be undefined)
// - fte 'discuss':                n/a (field should be undefined)
export type DayOffPreference =
  | 'wed-thu'     // 0.5 / 0.6 FTE
  | 'mon-fri'     // 0.5 / 0.6 FTE
  | 'wed-only'    // 0.75 / 0.8 FTE
  | 'fri-only'    // 0.75 / 0.8 FTE
  | 'none'        // no preference
  | 'discuss';    // flag for HR leader review

// Extended availability data structure for JSONB
export interface AvailabilityData {
  /**
   * Numeric FTE for downstream scoring/reporting.
   * Derived from ftePreference: '1' → 1.0, '0.8' → 0.8, '0.75' → 0.75, '0.6' → 0.6, '0.5' → 0.5, '0.4' → 0.4, 'discuss' → 1.0 (placeholder).
   * Use ftePreference for user-facing display/decisions.
   */
  fte: number;
  ftePreference?: FtePreference;
  dayOffPreference?: DayOffPreference;
  leavePlans?: string;             // Free text for leave/sabbatical plans

  // Deprecated — no longer collected by the form in v1.2.0+, retained for JSONB backward compat.
  daysAvailable?: string[];
  periodsUnavailable?: Array<{
    day: string;
    period: number;
  }>;
}

// Extended preferences data structure for JSONB
export interface PreferencesData {
  curriculumAreas: Array<{
    areaId: string;
    areaName: string;
    rank: number;
  }>;
  subjects: Array<{
    subjectId: string;
    subjectName: string;
    rank: number;
  }>;
  yearLevels: string[];            // ['year9', 'year10', ...]

  // RUAH-style subjects (Phase 4, v1.3.0):
  // These subjects sit on a separate grid from normal curriculum areas, so they're
  // captured in their own section of the form. Hardcoded list in RUAH_SUBJECTS.
  ruahSubjects?: Array<{
    subjectId: string;     // matches id in RUAH_SUBJECTS
    subjectName: string;   // display name
    rank: number;          // ordering by preference (1 = first choice)
  }>;
}

// Hardcoded RUAH-style subjects list.
// These subjects go on their own grid (RUAH / Wednesday VET / specialist grids),
// distinct from the main curriculum-area preference flow.
// Edit here to add/remove options; migrating to a `ruah_subjects` reference table
// is a later consideration once the list changes more frequently.
export const RUAH_SUBJECTS: ReadonlyArray<{ id: string; name: string; category: string }> = [
  // Core RUAH
  { id: 'ruah-generic',           name: 'RUAH',                                     category: 'RUAH' },
  { id: 'seminar',                name: 'Seminar',                                  category: 'RUAH' },

  // Discover programs
  { id: 'discover-re',            name: 'Discover Religious Education',             category: 'Discover' },
  { id: 'discover-hpe',           name: 'Discover Health & Physical Education',     category: 'Discover' },
  { id: 'discover-lang-italian',  name: 'Discover Language — Italian',              category: 'Discover' },
  { id: 'discover-lang-japanese', name: 'Discover Language — Japanese',             category: 'Discover' },

  // Explore / Life Skills
  { id: 'explore-health',         name: 'Explore Health',                           category: 'Explore' },
  { id: 'life-skills',            name: 'Life Skills',                              category: 'Life Skills' }
] as const;

export type RuahSubjectCategory = typeof RUAH_SUBJECTS[number]['category'];

// Staff submission for a period
export interface StaffIntentionSubmission {
  id: string;
  staffId: string;
  staffName: string;
  staffEmail?: string;
  department?: string;
  periodId: string;
  year: number;

  // Core submission data
  availability: AvailabilityData;
  preferences: PreferencesData;
  employmentDetails?: EmploymentDetailsData;  // New employment/leave/TIL data
  comments?: string;

  // Status tracking
  status: HRReviewStatus;
  submittedAt?: Date;
  lastUpdatedAt: Date;

  // Timing flag — set true on first submission if submittedAt > period.dueDate. Never flipped back.
  isLate?: boolean;

  // True when the submission needs HR-leader attention (e.g. 'discuss' on FTE or day-off preference).
  hrDiscussFlag?: boolean;

  // Audit: staff_id of the admin who submitted on behalf of this teacher (Phase 5 / admin fill-on-behalf).
  enteredByStaffId?: string;

  // HR review data
  hrReviewedBy?: string;
  hrReviewedAt?: Date;
  hrNotes?: string;
}

// Filter options for intention review
export interface IntentionReviewFilters {
  periodId?: string;
  status?: HRReviewStatus | 'all';
  department?: string;
  curriculumArea?: string;
  searchTerm?: string;
}

// Bulk action types for HR review
export type BulkReviewAction = 'approve' | 'flag';

export interface BulkReviewResult {
  success: boolean;
  processedCount: number;
  failedCount: number;
  errors?: string[];
}
